/**
 * Image URL Helper
 *
 * Purpose: Generate public CloudFront/S3 URLs for InClub avatars and headers
 * Uses AWS Serverless Image Handler with base64-encoded transformation requests
 * Similar to publicCDNUrlHelper.js in the React Native app
 */

// CloudFront configuration from environment
const CLOUDFRONT_DOMAIN = process.env.REACT_APP_CLOUDFRONT_DOMAIN;
const S3_BUCKET_NAME = process.env.REACT_APP_S3_BUCKET_NAME;

/**
 * Transformation presets
 * (Must match React Native app presets)
 */
export const IMAGE_PRESETS = {
  // User avatar - small circular profile images (optimized for bandwidth)
  userAvatar: {
    width: 120,
    height: 120,
    fit: 'cover',
    quality: 85,
    format: 'jpeg'
  },
  // Club avatar - small circular club images (optimized for bandwidth)
  clubAvatar: {
    width: 120,
    height: 120,
    fit: 'cover',
    quality: 85,
    format: 'jpeg'
  },
  // Club header - large banner images (2.4:1 aspect ratio)
  clubHeader: {
    width: 1920,
    height: 800,
    fit: 'cover',
    quality: 90,
    format: 'jpeg'
  },
  // Community card header - 16:9 aspect ratio for browse/home cards
  communityCardHeader: {
    width: 1920,
    height: 1080,
    fit: 'cover',
    quality: 90,
    format: 'jpeg'
  }
};

/**
 * Generate CloudFront URL with transformations
 * Uses AWS Serverless Image Handler
 *
 * @param {string} s3Key - S3 object key
 * @param {Object|string} options - Transformation options or preset name
 * @param {string} options.preset - Preset name (e.g., 'clubAvatar', 'clubHeader')
 * @param {number} options.version - Cache buster timestamp
 * @returns {string} CloudFront URL
 */
export const getImageUrl = (s3Key, options = {}) => {
  if (!s3Key) return null;
  if (!CLOUDFRONT_DOMAIN) {
    console.warn('⚠️ CLOUDFRONT_DOMAIN not configured');
    return null;
  }

  // Handle preset or custom options
  let transformations = options;
  if (typeof options === 'string') {
    // Preset name passed as string
    transformations = IMAGE_PRESETS[options] || {};
  } else if (options.preset) {
    transformations = IMAGE_PRESETS[options.preset] || options;
  }

  // Build edit request
  const edits = {
    // Auto-rotate based on EXIF orientation and strip the tag
    // This ensures consistent orientation across web and mobile platforms
    rotate: null
  };

  // Resize transformation
  if (transformations.width || transformations.height) {
    edits.resize = {
      width: transformations.width,
      height: transformations.height,
      fit: transformations.fit || 'inside'
    };
  }

  // Format conversion (default to jpeg)
  const format = transformations.format || 'jpeg';
  if (format === 'jpeg') {
    edits.jpeg = {
      quality: transformations.quality || 85,
      progressive: true
    };
  } else if (format === 'png') {
    edits.png = {
      quality: transformations.quality || 90
    };
  }

  // Build request object for AWS Serverless Image Handler
  const request = {
    bucket: S3_BUCKET_NAME,
    key: s3Key,
    edits
  };

  // CRITICAL: Include cache buster version INSIDE the request object
  // This ensures different timestamps create different base64 URLs
  if (options.version || options.cacheBuster) {
    request.v = options.version || options.cacheBuster;
  }

  // Base64 encode the request
  const encodedRequest = btoa(JSON.stringify(request));

  // Build CloudFront URL - add https:// if domain doesn't have it
  const baseUrl = CLOUDFRONT_DOMAIN.startsWith('http')
    ? CLOUDFRONT_DOMAIN
    : `https://${CLOUDFRONT_DOMAIN}`;

  return `${baseUrl}/${encodedRequest}`;
};

/**
 * Generate URL for InClub avatar
 * @param {string} inclubId - InClub ID
 * @param {number} cacheBuster - Optional timestamp to force cache refresh
 * @returns {string|null} URL for InClub avatar or null if CloudFront not configured
 */
export const getInclubAvatarUrl = (inclubId, cacheBuster = null) => {
  if (!inclubId) return null;

  // Static S3 filename (cache busting via timestamp in request)
  const s3Key = `inclub/${inclubId}/settings/avatar.jpg`;

  // Generate transformed URL with timestamp included in base64
  const url = getImageUrl(s3Key, {
    preset: 'clubAvatar',
    version: cacheBuster ? new Date(cacheBuster).getTime() : null
  });

  return url;
};

/**
 * Generate URL for InClub header
 * @param {string} inclubId - InClub ID
 * @param {number} cacheBuster - Optional timestamp to force cache refresh
 * @returns {string|null} URL for InClub header or null if CloudFront not configured
 */
export const getInclubHeaderUrl = (inclubId, cacheBuster = null) => {
  if (!inclubId) return null;

  // Static S3 filename (cache busting via timestamp in request)
  const s3Key = `inclub/${inclubId}/settings/header.jpg`;

  // Generate transformed URL with timestamp included in base64
  const url = getImageUrl(s3Key, {
    preset: 'clubHeader',
    version: cacheBuster ? new Date(cacheBuster).getTime() : null
  });

  return url;
};

/**
 * Generate URL for Room header image (used for default room display)
 * @param {string} inclubId - InClub ID
 * @param {string} roomId - Room ID
 * @param {number} cacheBuster - Optional timestamp to force cache refresh
 * @returns {string|null} URL for room header or null if CloudFront not configured
 */
export const getRoomHeaderUrl = (inclubId, roomId, cacheBuster = null) => {
  if (!inclubId || !roomId) return null;

  // Static S3 filename (cache busting via timestamp in request)
  const s3Key = `inclub/${inclubId}/rooms/${roomId}/header.jpg`;

  // Generate transformed URL with timestamp included in base64
  const url = getImageUrl(s3Key, {
    preset: 'clubHeader',
    version: cacheBuster ? new Date(cacheBuster).getTime() : null
  });

  return url;
};

/**
 * Generate URL for Room avatar image (used for default room display)
 * @param {string} inclubId - InClub ID
 * @param {string} roomId - Room ID
 * @param {number} cacheBuster - Optional timestamp to force cache refresh
 * @returns {string|null} URL for room avatar or null if CloudFront not configured
 */
export const getRoomAvatarUrl = (inclubId, roomId, cacheBuster = null) => {
  if (!inclubId || !roomId) return null;

  // Static S3 filename (cache busting via timestamp in request)
  const s3Key = `inclub/${inclubId}/rooms/${roomId}/avatar.jpg`;

  // Generate transformed URL with timestamp included in base64
  const url = getImageUrl(s3Key, {
    preset: 'clubAvatar',
    version: cacheBuster ? new Date(cacheBuster).getTime() : null
  });

  return url;
};

/**
 * Check if an image exists at the given URL
 * @param {string} url - Image URL to check
 * @returns {Promise<boolean>} True if image exists, false otherwise
 */
export const checkImageExists = async (url) => {
  if (!url) return false;
  try {
    const response = await fetch(url, { method: 'HEAD' });
    return response.ok;
  } catch (error) {
    return false;
  }
};

/**
 * Generate URL for user avatar
 * @param {string} userId - User ID
 * @param {number} cacheBuster - Optional timestamp to force cache refresh
 * @returns {string|null} URL for user avatar or null if CloudFront not configured
 */
export const getUserAvatarUrl = (userId, cacheBuster = null) => {
  if (!userId) return null;

  // Static S3 filename (cache busting via timestamp in request)
  const s3Key = `users/${userId}/avatar.jpg`;

  // Generate transformed URL with timestamp included in base64
  const url = getImageUrl(s3Key, {
    preset: 'userAvatar',
    version: cacheBuster ? new Date(cacheBuster).getTime() : null
  });

  return url;
};

/**
 * Generate club initials from name for avatar fallback
 * @param {string} name - Club name
 * @returns {string} Two-letter initials
 */
export const generateClubInitials = (name) => {
  if (!name) return 'CL';
  return name
    .split(' ')
    .map(word => word.charAt(0))
    .join('')
    .substring(0, 2)
    .toUpperCase();
};

/**
 * Generate user initials for avatar fallback
 * @param {Object} userData - User data object
 * @returns {string} Two-letter initials
 */
export const generateUserInitials = (userData) => {
  if (!userData) return 'U';

  if (userData.firstName && userData.lastName) {
    return `${userData.firstName.charAt(0)}${userData.lastName.charAt(0)}`.toUpperCase();
  }
  if (userData.firstName) {
    return userData.firstName.charAt(0).toUpperCase();
  }
  if (userData.name) {
    const names = userData.name.split(' ');
    if (names.length > 1) {
      return `${names[0].charAt(0)}${names[names.length - 1].charAt(0)}`.toUpperCase();
    }
    return userData.name.charAt(0).toUpperCase();
  }
  if (userData.email) {
    return userData.email.charAt(0).toUpperCase();
  }
  return 'U';
};
